import ODataStore from 'devextreme/data/odata/store'
import DataSource from 'devextreme/data/data_source'
import { memo, useRef, useState } from 'react'
import { ODataStoreRequestConfiguration } from '@/auth/api/config'
import type {
  qs_applicazione,
  qs_licenza,
  qs_versione,
  tipologia_qs_licenza,
} from '@/model/qsadminapi/QsAdminApiModuleModel'
import { FormMode } from '@/enums'
import {
  Column,
  DataGrid,
  DataGridRef,
  Editing,
  Item,
  Lookup,
  Paging,
  RequiredRule,
  StateStoring,
  Toolbar,
} from 'devextreme-react/data-grid'
import type { InitNewRowEvent } from 'devextreme/ui/data_grid'
import { useTokenRefresh } from '@/auth/azure/azureManager'
import { useScreenSize } from '@/themes/media-query'
import { ClickEvent } from 'devextreme/ui/button'
import Popup, { PopupRef } from 'devextreme-react/cjs/popup'
import Form, { ButtonItem, FormRef, GroupItem, SimpleItem } from 'devextreme-react/cjs/form'
import { ArraySourceComposer } from '@/auth/api/connector'
import { DateBoxTypes } from 'devextreme-react/cjs/date-box'

type SoftwareQsEditorFormAppsProps = {
  license: qs_licenza
  mode: FormMode
  qsVersions: qs_versione[]
  qsLicenseTypes: tipologia_qs_licenza[]
  qsApps: qs_applicazione[]
  readOnly?: boolean
}

export const SoftwareQsEditorFormApps = (props: SoftwareQsEditorFormAppsProps) => {
  const [currentQsLicenseData] = useState<qs_licenza>(props.license)
  const today = new Date().toISOString()
  const token = useTokenRefresh()
  const currentScreenSize = useScreenSize()
  const qsLicenseApplicationsDataSource = new DataSource({
    store: new ODataStore({
      url: `${import.meta.env.VITE_QSADMINAPI_HOST}/qs_licenza_applicazione`,
      key: 'id',
      version: 4,
      beforeSend: ODataStoreRequestConfiguration(token),
      deserializeDates: false,
    }),
    filter: [['licenza.id', currentQsLicenseData?.id]],
    expand: ['applicazione', 'tipologia_licenza', 'ultima_versione', 'licenza'],
  })
  const gridRef = useRef<DataGridRef>(null)
  const popupRef = useRef<PopupRef>(null)
  const formRef = useRef<FormRef>(null)

  return (
    <>
      <DataGrid
        ref={gridRef}
        id={`sw-qsapps-grid-${currentQsLicenseData?.id}`}
        dataSource={qsLicenseApplicationsDataSource}
        keyExpr={'id'}
        showBorders={true}
        showColumnLines={true}
        focusedRowEnabled={true}
        columnHidingEnabled={currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium}
        allowColumnReordering={true}
        allowColumnResizing={true}
        rowAlternationEnabled={true}
        wordWrapEnabled={false}
        repaintChangesOnly={true}
        width="100%"
        onInitNewRow={(e: InitNewRowEvent) => {
          e.data.aggiornamenti = false
          e.data.passaggio_noleggio = false
          e.data.data_associazione = today
          e.data.licenza = currentQsLicenseData
        }}
      >
        <StateStoring enabled={true} type="localStorage" storageKey={'swqs-apps-datagrid'} savingTimeout={50} />
        <Paging defaultPageSize={8} />
        <Editing
          mode="batch"
          allowUpdating={props.mode !== FormMode.View && !props.readOnly}
          allowAdding={props.mode !== FormMode.View && !props.readOnly}
          allowDeleting={props.mode !== FormMode.View && !props.readOnly}
          selectTextOnEditStart={true}
          startEditAction={'click'}
          onChangesChange={(value: any) => {
            // Triggerato quando avviene una modifica. Se l'array length è vuoto significa che la modifica è stata annullata o salvata....
            //console.log('CHHHHHH', value)
            //setAppsAreDirty(value.length > 0)
          }}
        />
        <Column dataField={'id'} width={'3%'} dataType={'number'} visible={false} />,
        <Column
          dataField={'data_associazione'}
          caption={'DATA'}
          width={'12%'}
          dataType={'date'}
          format={'dd/MM/yyyy'}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 1 : undefined
          }
        />
        <Column
          dataField={'applicazione.id'}
          caption={'APPLICAZIONE'}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 7 : undefined
          }
        >
          <RequiredRule message="Applicazione obbligatoria"></RequiredRule>
          <Lookup dataSource={props.qsApps} displayExpr="nome" valueExpr="id" />
        </Column>
        <Column
          dataField={'tipologia_licenza.id'}
          caption={'TIPO'}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 5 : undefined
          }
        >
          <RequiredRule message="Tipologia obbligatoria"></RequiredRule>
          <Lookup dataSource={props.qsLicenseTypes} displayExpr="nome" valueExpr="id" />
        </Column>
        <Column
          dataField={'ultima_versione.id'}
          caption={'UV'}
          width={'5%'}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 6 : undefined
          }
        >
          <Lookup dataSource={props.qsVersions} displayExpr="major" valueExpr="id" />
        </Column>
        <Column
          dataField={'scadenza'}
          caption={'SCADENZA'}
          width={'12%'}
          dataType={'date'}
          format={'dd/MM/yyyy'}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 4 : undefined
          }
        />
        <Column
          dataField={'passaggio_noleggio'}
          caption={'SWITCH'}
          width={'10%'}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 3 : undefined
          }
        />
        <Column
          dataField={'aggiornamenti'}
          caption={'US'}
          width={'5%'}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 2 : undefined
          }
        />
        <Toolbar>
          <Item
            location="before"
            locateInMenu="never"
            showText="inMenu"
            widget="dxButton"
            options={{
              hint: 'Refresh',
              text: 'Refresh',
              icon: 'refresh',
              onClick: (e: ClickEvent) => {
                gridRef.current?.instance().refresh()
              },
              stylingMode: 'text',
            }}
          ></Item>
          <Item name="addRowButton"></Item>
          <Item
            location="after"
            locateInMenu="never"
            showText="inMenu"
            widget="dxButton"
            options={{
              hint: 'Aggiungi tutte le applicazioni Qs',
              text: 'Aggiungi tutte le applicazioni Qs',
              icon: 'fas fa-square-plus',
              onClick: (e: ClickEvent) => {
                popupRef.current?.instance().show()
              },
              stylingMode: 'text',
            }}
          ></Item>
          <Item name="saveButton"></Item>
          <Item name="revertButton"></Item>
        </Toolbar>
      </DataGrid>
      <div>
        <Popup
          width={currentScreenSize.isLarge || currentScreenSize.isMedium ? 500 : currentScreenSize.isXSmall ? 350 : 700}
          height={currentScreenSize.isSmall ? 320 : currentScreenSize.isXSmall ? 720 : 'auto'}
          hideOnOutsideClick={true}
          title="Aggiungi tutte le applicazioni Qs"
          showCloseButton={true}
          deferRendering={true}
          resizeEnabled={false}
          ref={popupRef}
        >
          <div id="sw-qsapps-grid-all-apps-form-container" className={'dx-card responsive-paddings'}>
            <Form
              id={`sw-qsapps-grid-all-apps-form`}
              key={`sw-qsapps-grid-all-apps-form`}
              formData={{
                ultima_versione: props.license.versione_uso,
                scadenza: props.license.scadenza,
                tipologia_licenza: props.qsLicenseTypes.filter((type) => type.nome === 'VENDITA')[0],
              }}
              ref={formRef}
              validationGroup="allAppsValidation"
            >
              <GroupItem colCount={1}>
                <SimpleItem
                  dataField="scadenza"
                  editorOptions={{
                    displayFormat: 'dd/MM/yyyy',
                    type: 'date',
                    pickerType: 'calendar',
                    applyValueMode: 'instantly',
                    placeholder: 'Inserisci scadenza...',
                    onOpened: (e: DateBoxTypes.OpenedEvent) => {
                      formRef.current?.instance().getButton('btn-add')?.option('disabled', true)
                    },
                    onClosed: (e: DateBoxTypes.ClosedEvent) => {
                      formRef.current?.instance().getButton('btn-add')?.option('disabled', false)
                    },
                  }}
                  editorType="dxDateBox"
                  label={{ text: 'Scadenza' }}
                ></SimpleItem>
                <SimpleItem
                  dataField="ultima_versione"
                  label={{ text: 'Versione' }}
                  editorType="dxLookup"
                  editorOptions={{
                    dataSource: ArraySourceComposer('id', props.qsVersions),
                    placeholder: 'Seleziona la versione...',
                    displayExpr: 'major',
                    searchEnabled: true,
                    dropDownCentered: true,
                    dropDownOptions: {
                      showTitle: true,
                      title: 'Selezionare la versione',
                      hideOnOutsideClick: true,
                    },
                  }}
                ></SimpleItem>
                <SimpleItem
                  dataField="tipologia_licenza"
                  label={{ text: 'Tipologia licenza' }}
                  editorType="dxLookup"
                  editorOptions={{
                    dataSource: ArraySourceComposer('id', props.qsLicenseTypes),
                    placeholder: 'Seleziona la tipologia...',
                    displayExpr: 'nome',
                    searchEnabled: true,
                    dropDownCentered: true,
                    dropDownOptions: {
                      showTitle: true,
                      title: 'Selezionare la tipologia',
                      hideOnOutsideClick: true,
                    },
                  }}
                ></SimpleItem>
              </GroupItem>
              <GroupItem cssClass="last-group">
                <GroupItem cssClass="buttons-group" colCount={1}>
                  <ButtonItem
                    name="btn-add"
                    buttonOptions={{
                      icon: 'fas fa-square-plus',
                      text: 'Aggiungi',
                      width: '150px',
                      onClick: async (e: ClickEvent) => {
                        const currentChanges = gridRef.current?.instance().option('editing.changes') as any[]
                        const newChanges = props.qsApps.map((qsApp) => ({
                          type: 'insert',
                          data: {
                            aggiornamenti: false,
                            passaggio_noleggio: false,
                            data_associazione: today,
                            licenza: currentQsLicenseData,
                            applicazione: qsApp,
                            ultima_versione: formRef.current?.instance().getEditor('ultima_versione')?.option().value,
                            scadenza: formRef.current?.instance().getEditor('scadenza')?.option().value,
                            tipologia_licenza: formRef.current?.instance().getEditor('tipologia_licenza')?.option()
                              .value,
                          },
                        }))
                        gridRef.current?.instance().option('editing.changes', [...currentChanges, ...newChanges])
                        popupRef.current?.instance().hide()
                      },
                    }}
                  ></ButtonItem>
                </GroupItem>
              </GroupItem>
            </Form>
          </div>
        </Popup>
      </div>
    </>
  )
}

const SoftwareQsAppsGridMemoed = memo(SoftwareQsEditorFormApps, (oldProps, newProps) => {
  return oldProps.license.id === newProps?.license.id
})

export default SoftwareQsAppsGridMemoed
